import { useIntl } from 'react-intl-next';

import { type UserConvoAIStreamingConfig } from '@atlassian/ai-agent-streaming';
import type { AiFeatureRequestEnvelopeSuggestIssuesDocumentationRequest } from '@atlassian/convo-ai-api';

import { fg } from '@confluence/feature-gating';
import { mapActionItemsToIssues } from './actionItemUtils';
import { useIssueCreateSidePanelContext } from '../providers/IssueCreateSidePanelContextProvider';
import { useBulkCreateContext } from '../providers/BulkCreateContextProvider';
import { getRowCountWithContent } from './tableUtils';

type SelectionInfo = {
	isTableCreation: boolean;
	isActionItemCreation: boolean;
	isUserSelectedCreation: boolean;
	selectedContent: HTMLElement | undefined;
};

/**
 * Helper function to determine the creation type and selected content
 * based on the selected table or action items.
 */
const getSelectionInfo = (
	isCreatedFromTable: boolean,
	isCreatedFromActionItems: boolean,
	selectedTable: any,
	selectedActionItems: any,
): SelectionInfo => {
	const isTableCreation =
		isCreatedFromTable && selectedTable && fg('confluence-create-work-items-from-action-items');
	// table creation has higher priority than action item creation
	const isActionItemCreation =
		!isTableCreation &&
		isCreatedFromActionItems &&
		selectedActionItems &&
		fg('confluence-create-work-items-from-action-items');
	const isUserSelectedCreation = isTableCreation || isActionItemCreation;
	let selectedContent: HTMLElement | undefined;
	if (isTableCreation) {
		selectedContent = selectedTable;
	} else if (isActionItemCreation) {
		selectedContent = selectedActionItems;
	}
	return {
		isTableCreation,
		isActionItemCreation,
		isUserSelectedCreation,
		selectedContent,
	};
};


const calculateMaxIssues = (
    isTableCreation: boolean,
    isActionItemCreation: boolean,
    selectedTable?: HTMLTableElement,
    selectedActionItems?: HTMLElement,
): number => {
    if (isTableCreation && selectedTable) return getRowCountWithContent(selectedTable.rows);
    if (isActionItemCreation && selectedActionItems) return mapActionItemsToIssues(selectedActionItems).length;
    return 10;
};

export const useBuildIssueSuggestionsRequest = (
	markdownPlusContent: string,
	pageAri: string,
	isConvoAIMigrationEnabled: boolean,
	overrideXProduct?: string,
): [AiFeatureRequestEnvelopeSuggestIssuesDocumentationRequest, UserConvoAIStreamingConfig] => {
	const { locale } = useIntl();
	const [{ isCreatedFromActionItems, isCreatedFromTable }] = useBulkCreateContext();
	const [{ selectedActionItems, selectedTable }] = useIssueCreateSidePanelContext();

	const { isTableCreation, isActionItemCreation, isUserSelectedCreation, selectedContent } =
		getSelectionInfo(isCreatedFromTable, isCreatedFromActionItems, selectedTable, selectedActionItems);

	const max_issues = calculateMaxIssues(isTableCreation, isActionItemCreation, selectedTable, selectedActionItems);
	
	return [
		{
			ai_feature_input: {
				context: {
					primary_text: isUserSelectedCreation ? ' ' : markdownPlusContent,
					...(isUserSelectedCreation ? { highlighted_text: selectedContent?.innerHTML } : {}),
					input_mime_type: 'text/markdown',
					source_ari: pageAri,
				},
				source: 'CONFLUENCE',
				locale,
				suggested_issues_config: {
					max_issues,
					suggested_issue_field_types: [
						{
							issue_type: 'Task',
							fields: [
								{
									field_name: 'Summary',
									field_type: 'Short-Text',
								},
								{
									field_name: 'Description',
									field_type: 'Paragraph',
								},
							],
						},
					],
				},
				sourceType: 'string',
			},
		},
		{
			apiPath: 'v2/ai-feature/jira/issue/source-type/documentation/suggestions/stream',
			enablePartialJson: false,
			skip: !markdownPlusContent || !isConvoAIMigrationEnabled,
			headers: {
				'x-experience-id': 'ai-issue-create-confluence-bulk',
				'x-product':
					overrideXProduct && fg('confluence-create-work-items-from-action-items')
						? overrideXProduct
						: 'confluence',
			},
		},
	];
};
